import { lazy, Suspense } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import heroPen from "@/assets/mesa1-gold-fordite.webp";
import heroThumbA from "@/assets/bs1-red-pink-bottle-stopper.webp";
import heroThumbB from "@/assets/drt-ab2-dragon-antique-brass.webp";
import workshop from "@/assets/carving-dished-eagle-1.webp";
import makerShot from "@/assets/cornhole-red.webp";
import { homeCategories, homeFeatured, homePrevious } from "@/data/home-catalog";
import { craftSteps, faqs, trustPoints } from "@/data/content";
import { site, pageUrl } from "@/data/site";
import { Section, SectionHeading } from "@/components/site/section";
import { CtaLink } from "@/components/site/cta";
import { FilterableGrid, ProductGrid } from "@/components/site/product-grid";
import { FinalCTA, ProcessTimeline } from "@/components/site/final-cta";
import { FAQAccordion } from "@/components/site/faq-accordion";
import { DeferIsland } from "@/components/site/defer-island";

const GiftFinder = lazy(() =>
  import("@/components/site/gift-finder").then((m) => ({ default: m.GiftFinder })),
);
const MaterialExplorer = lazy(() =>
  import("@/components/site/material-explorer").then((m) => ({ default: m.MaterialExplorer })),
);

function GiftFinderFallback() {
  return (
    <div className="surface-card overflow-hidden" aria-hidden="true">
      <div className="h-14 border-b border-hairline bg-paper" />
      <div className="min-h-[18rem] p-6 sm:p-8" />
    </div>
  );
}

function MaterialExplorerFallback() {
  return (
    <div className="mt-10 grid min-h-[28rem] gap-8 lg:grid-cols-[1fr_1.1fr]" aria-hidden="true">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {Array.from({ length: 9 }, (_, i) => (
          <div key={i} className="h-[6.25rem] rounded-sm border border-hairline" />
        ))}
      </div>
      <div className="surface-card min-h-[22rem]" />
    </div>
  );
}

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Paul's Pens n Things | Handcrafted Pens & Gifts in Arkansas" },
      {
        name: "description",
        content:
          "One-of-a-kind handcrafted pens, lathe-turned gifts and CNC-carved pieces made around your story by Paul Sherman in Paragould, Arkansas.",
      },
      { property: "og:title", content: "Paul's Pens n Things | Handcrafted Pens & Gifts in Arkansas" },
      {
        property: "og:description",
        content:
          "One-of-a-kind handcrafted pens, lathe-turned gifts and CNC-carved pieces made around your story by Paul Sherman in Paragould, Arkansas.",
      },
      { property: "og:url", content: pageUrl("/") },
    ],
    links: [
      { rel: "canonical", href: pageUrl("/") },
      { rel: "preload", href: heroPen, as: "image", fetchPriority: "high" },
    ],
  }),
  component: Home,
});

const categoryCards = [
  homeCategories.pens,
  homeCategories["lathe-gifts"],
  homeCategories.cnc,
  homeCategories.other,
  homeCategories["pen-turners"],
];

function Home() {
  return (
    <>
      {/* Hero */}
      <section aria-labelledby="hero-heading" className="relative overflow-hidden bg-canvas">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-24 top-10 size-[26rem] rounded-full bg-brass/10 blur-3xl"
        />
        <div className="shell relative grid items-center gap-14 py-[clamp(2.5rem,5.5vw,5rem)] lg:grid-cols-12 lg:gap-10">
          <div className="rise lg:col-span-7">
            <div className="flex items-center gap-3">
              <span aria-hidden="true" className="h-px w-12 bg-brass" />
              <p className="text-[0.62rem] font-bold uppercase tracking-[0.3em] text-walnut">
                Handcrafted in Paragould, Arkansas, since 2006
              </p>
            </div>

            <h1
              id="hero-heading"
              className="mt-6 font-[family-name:var(--font-display)] text-[clamp(2.9rem,7.4vw,6rem)] font-black leading-[0.86] tracking-[-0.03em]"
            >
              Handcrafted <span className="font-light italic text-forest">Pens</span>
              <br />
              &amp;{" "}
              <span className="underline decoration-brass decoration-2 underline-offset-[10px]">Custom</span> Gifts
            </h1>

            <p className="lede mt-8 max-w-lg font-light">
              Explore Paul&apos;s previous creations or tell him what you have in mind. Every piece is
              handcrafted around your preferred materials, style and occasion.
            </p>

            <div className="mt-9 flex flex-col gap-4 sm:flex-row">
              <CtaLink to="/design-your-piece" arrow className="h-14 px-9 text-xs uppercase tracking-[0.18em]">
                Design Your Piece
              </CtaLink>
              <CtaLink
                to="/gallery"
                variant="outline"
                className="h-14 px-9 text-xs uppercase tracking-[0.18em]"
              >
                Explore Recent Creations
              </CtaLink>
            </div>

            <ul className="mt-12 grid w-full gap-6 border-t border-hairline pt-8 sm:grid-cols-2 lg:grid-cols-4">
              {trustPoints.map((t, i) => (
                <li key={t}>
                  <span className="block text-[0.7rem] font-bold uppercase tracking-[0.2em] text-brass-ink">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="mt-1 block text-sm font-medium leading-snug">{t}</span>
                </li>
              ))}
            </ul>
          </div>

          <figure className="relative mx-auto w-full max-w-[440px] lg:col-span-5 lg:mx-0 lg:ml-auto">
            <div className="relative aspect-[4/5] w-full">
              <div className="relative z-20 h-full w-full overflow-hidden bg-paper shadow-[var(--shadow-raise)]">
                <img
                  src={heroPen}
                  alt="Sierra Mesa Gatsby pen turned in Fordite with gold trim, handcrafted by Paul"
                  width={720}
                  height={900}
                  fetchPriority="high"
                  decoding="async"
                  className="h-full w-full object-contain p-6"
                />
              </div>


              <img
                src={heroThumbA}
                alt="Red and pink cast bottle stopper on a stainless steel base"
                width={320}
                height={420}
                loading="lazy"
                decoding="async"
                className="absolute -bottom-14 -left-10 z-30 hidden h-44 w-32 border-[10px] border-paper bg-card object-contain shadow-[var(--shadow-raise)] 2xl:block"
              />


              <div
                aria-hidden="true"
                className="absolute -left-6 -top-6 z-40 size-32 motion-safe:animate-[spin_36s_linear_infinite]"
              >
                <svg viewBox="0 0 100 100" className="size-full text-brass opacity-60">
                  <path
                    id="heroSeal"
                    d="M 50, 50 m -37, 0 a 37,37 0 1,1 74,0 a 37,37 0 1,1 -74,0"
                    fill="none"
                  />
                  <text fontSize="8" fontWeight="700" className="fill-current uppercase tracking-widest">
                    <textPath href="#heroSeal">Authentic • Handcrafted • Paragould Arkansas •</textPath>
                  </text>
                </svg>
              </div>
            </div>

            <img
              src={heroThumbB}
              alt="Dragon rollerball pen with antique brass trim"
              width={200}
              height={260}
              loading="lazy"
              decoding="async"
              className="absolute -right-4 top-8 z-30 hidden h-40 w-28 bg-paper object-contain p-2 shadow-[var(--shadow-lift)] lg:block"
            />

            <figcaption className="mt-6 flex items-baseline justify-end gap-3 text-right">
              <span className="font-[family-name:var(--font-display)] text-base italic">
                Sierra Mesa Gatsby in Fordite
              </span>
              <span className="text-[0.7rem] uppercase tracking-[0.2em] text-brass-ink">One of one</span>
            </figcaption>

          </figure>
        </div>
      </section>


      {/* Categories */}
      <Section tone="paper" labelledBy="cat-heading">
        <div className="shell">
          <SectionHeading
            id="cat-heading"
            eyebrow="What Paul makes"
            title="Five benches, one pair of hands."
            lede="Everything here is made one at a time. Start where your idea fits best — Paul will help with the rest."
          />
          <ul className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {categoryCards.map((c, i) => (
              <li key={c.slug} className={i === 0 ? "sm:col-span-2 lg:col-span-2" : undefined}>
                <Link
                  to={c.to}
                  className="group relative flex h-full flex-col overflow-hidden surface-card motion-craft transition-shadow hover:shadow-[var(--shadow-raise)]"
                >
                  <div className="overflow-hidden bg-paper">
                    <img
                      src={c.image}
                      alt={c.alt}
                      width={1200}
                      height={900}
                      loading="lazy"
                      decoding="async"
                      className={`w-full object-cover motion-craft transition-transform duration-500 group-hover:scale-[1.04] ${
                        i === 0 ? "aspect-[16/9]" : "aspect-[4/3]"
                      }`}
                    />
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="font-[family-name:var(--font-display)] text-2xl">{c.label}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.blurb}</p>
                    <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-walnut">
                      Explore
                      <svg
                        aria-hidden="true"
                        viewBox="0 0 16 16"
                        className="size-4 motion-craft transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                      >
                        <path
                          d="M4 12 12 4M6.5 4H12v5.5"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </Section>

      {/* Featured creations */}
      <Section labelledBy="featured-heading">
        <div className="shell">
          <SectionHeading
            id="featured-heading"
            eyebrow="Recent creations"
            title="Pieces from the bench."
            lede="Some are ready to order, some have already found their homes. All of them can become the starting point for yours."
            action={
              <CtaLink to="/gallery" variant="outline" arrow className="h-11">
                View all creations
              </CtaLink>
            }
          />
          <FilterableGrid source={homeFeatured} context="home_featured" />
        </div>
      </Section>

      {/* Gift finder */}
      <Section tone="paper" labelledBy="gift-heading">
        <div className="shell grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:items-start">
          <SectionHeading
            id="gift-heading"
            eyebrow="Not sure where to start"
            title="Find the right gift."
            lede="Four short questions. No account, no pressure — just a sensible place to begin the conversation with Paul."
          />
          <DeferIsland fallback={<GiftFinderFallback />}>
            <Suspense fallback={<GiftFinderFallback />}>
              <GiftFinder />
            </Suspense>
          </DeferIsland>
        </div>
      </Section>

      {/* Why handcrafted matters */}
      <Section tone="ink" labelledBy="craft-heading">
        <div className="shell grid gap-12 lg:grid-cols-[1fr_1fr] lg:items-center">
          <div>
            <SectionHeading
              id="craft-heading"
              eyebrow="Why handcrafted matters"
              title="Made slowly, so it can mean more."
              lede="A handmade piece costs more than a mass-produced one because of where the hours go. Here is what those hours buy."
              tone="light"
            />
            <ol className="mt-10 space-y-6">
              {craftSteps.map((s, i) => (
                <li key={s.title} className="flex gap-5 border-t border-canvas/15 pt-5">
                  <span className="font-[family-name:var(--font-display)] text-sm text-brass-ink">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3 className="text-base font-semibold text-canvas">{s.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-canvas/65">{s.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
          <img
            src={workshop}
            alt="CNC-carved dished eagle relief panel made in Paul's shop"
            width={1408}
            height={1008}
            loading="lazy"
            decoding="async"
            className="aspect-[4/3] w-full object-cover"
          />
        </div>
      </Section>

      {/* Material explorer */}
      <Section labelledBy="materials-heading">
        <div className="shell">
          <SectionHeading
            id="materials-heading"
            eyebrow="Material library"
            title="The material decides half the piece."
            lede="Choose a material to see how it behaves, what it suits and where Paul has used it before."
          />
          <DeferIsland fallback={<MaterialExplorerFallback />}>
            <Suspense fallback={<MaterialExplorerFallback />}>
              <MaterialExplorer />
            </Suspense>
          </DeferIsland>
        </div>
      </Section>

      {/* Process */}
      <Section tone="paper" labelledBy="process-heading">
        <div className="shell">
          <SectionHeading
            id="process-heading"
            eyebrow="How custom ordering works"
            title="Four steps, no surprises."
            lede="Nothing is charged and nothing is made until you have seen the details and agreed to them."
          />
          <ProcessTimeline />
        </div>
      </Section>

      {/* Previously created */}
      <Section labelledBy="prev-heading">
        <div className="shell">
          <SectionHeading
            id="prev-heading"
            eyebrow="Previously created"
            title="Made before. Reimagined for you."
            lede="These original pieces have found their homes, but they can inspire a new creation using your preferred materials, colors and details."
            action={
              <CtaLink to="/gallery?status=previously-created" variant="outline" arrow className="h-11">
                View all previously created
              </CtaLink>
            }
          />
          <ProductGrid products={homePrevious} />
        </div>
      </Section>

      {/* Meet Paul */}
      <Section tone="paper" labelledBy="maker-heading">
        <div className="shell grid gap-12 lg:grid-cols-[1fr_1.05fr] lg:items-center">
          <img
            src={makerShot}
            alt="Red custom cornhole boards built and finished by Paul in Paragould"
            width={1408}
            height={1008}
            loading="lazy"
            decoding="async"
            className="aspect-[5/4] w-full object-cover"
          />
          <div>
            <SectionHeading
              id="maker-heading"
              eyebrow="Meet the maker"
              title="Meet the maker behind every piece."
              lede="Paul began making handcrafted pens and other lathe-turned items in 2006. In 2019, he added a CNC router to expand into carved and engraved creations."
            />
            <p className="mt-6 max-w-xl text-sm leading-relaxed text-muted-foreground">
              Every piece on this site was made by Paul himself, in his shop in Paragould. When you send a
              request, he is the one who reads it, replies to it and makes the piece. That is the whole
              operation, and it is deliberate.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <CtaLink to="/about" variant="outline" arrow className="h-11">
                Read Paul&apos;s story
              </CtaLink>
              <a
                href={`tel:${site.phoneHref}`}
                className="tap-safe inline-flex items-center justify-center rounded-sm px-4 text-sm font-semibold text-walnut underline underline-offset-4"
              >
                {site.phoneDisplay}
              </a>
            </div>
          </div>
        </div>
      </Section>

      {/* Testimonials intentionally hidden until Paul supplies approved reviews. */}

      {/* FAQ preview */}
      <Section labelledBy="faq-heading">
        <div className="shell grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
          <SectionHeading
            id="faq-heading"
            eyebrow="Common questions"
            title="Before you ask."
            lede="The questions Paul is asked most often. If yours is not here, just ask him."
            action={
              <CtaLink to="/faq" variant="outline" className="h-11">
                Read the full FAQ
              </CtaLink>
            }
          />
          <FAQAccordion items={faqs.slice(0, 6)} />
        </div>
      </Section>

      <FinalCTA />
    </>
  );
}
